/**
 * getAuthToken — synchronous-ish Firebase ID token helper.
 * Returns the cached token via onIdTokenChanged subscription so it's
 * always fresh (Firebase rotates every ~1h automatically).
 * Both brand and admin use this; the shared apiClient interceptor also
 * uses auth.currentUser.getIdToken() but this helper is useful for
 * SSE hooks and deep-analysis callbacks that need a raw Bearer string.
 */
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from './firebaseClient';

let _cachedToken: string | null = null;

// Keep in sync with Firebase token rotation (~1h) and sign-out.
onIdTokenChanged(auth, async (user) => {
  _cachedToken = user ? await user.getIdToken() : null;
});

/** Returns the current Firebase ID token (null if not signed in). */
export const getAuthToken = (): string | null => _cachedToken;

/**
 * Returns a fresh token via getIdToken(). Use for one-off calls that
 * need the most up-to-date token (e.g. SSE connections that re-open).
 */
export const getAuthTokenAsync = (forceRefresh = false): Promise<string> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  return user.getIdToken(forceRefresh);
};
